// ============================================================================
// LE CODE ARTICLE PROPOSÉ — dérivé de la désignation quand rien d'autre ne le donne.
//
// UTILITÉ : le code article regroupe les déclinaisons d'un même modèle (couleurs × tailles). Les écrans de
// création le PROPOSENT (modifiable), l'import de fichier le POSE quand le fichier n'en a pas : la même
// désignation doit donner le même code partout, sinon un article créé à l'écran et sa déclinaison
// importée deviendraient deux modèles.
//
// ⚠️ LA MÊME DÉSIGNATION GARDE LE MÊME CODE ; UNE AUTRE DÉSIGNATION QUI TOMBE SUR UN CODE PORTÉ REÇOIT UN SUFFIXE
//    (`suffixedArticleCode`). Coupées à 24 caractères, deux désignations longues qui commencent pareil donnent le même
//    code : sans suffixe, le second article deviendrait une déclinaison du premier, et ses champs écraseraient les siens.
// ⚠️ « Œ » ET « Æ » SE DÉPLIENT (« CŒUR » → « COEUR ») : la décomposition Unicode ne les sépare pas, et sans
//    ce dépliage la lettre devenait un tiret (« C-UR »).
// ============================================================================

/** Longueur maximale d'un code proposé — celle que les écrans affichent sans le couper. */
export const ARTICLE_CODE_MAX = 24;

/** Ce que rend une désignation sans aucune lettre ni chiffre exploitable. */
export const ARTICLE_CODE_FALLBACK = 'ART';

/** Le code article proposé pour une désignation : majuscules sans accents, mots reliés par des tirets. */
export function proposeArticleCode(name: string): string {
  const base = name.trim().toUpperCase()
    .replace(/Œ/g, 'OE').replace(/Æ/g, 'AE')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  // ⚠️ LA COUPE PEUT TOMBER SUR UN TIRET : on le retire après coup, sinon « CHEMISE-OXFORD-MANCHES-» finit
  //    par un séparateur qui ne sépare rien.
  return base.slice(0, ARTICLE_CODE_MAX).replace(/-+$/, '') || ARTICLE_CODE_FALLBACK;
}

/** Le dernier suffixe essayé — au-delà, aucun code libre n'est proposé et l'écran demande d'en saisir un. */
export const ARTICLE_CODE_SUFFIX_MAX = 99;

/**
 * LE CODE ARTICLE SUFFIXÉ — « BASE-2 », « BASE-3 »… pour une désignation dont le code de base est porté par un autre
 * article. ⚠️ LA BASE EST COUPÉE À 21 CARACTÈRES, que le suffixe ait un chiffre ou deux : le code tient dans les 24 d'un
 * code proposé, et l'import de fichier et les écrans de création donnent le même code pour la même désignation.
 */
export function suffixedArticleCode(base: string, suffix: number): string {
  return `${base.slice(0, ARTICLE_CODE_MAX - 3).replace(/-+$/, '')}-${suffix}`;
}

/** Les codes à essayer pour une base, dans l'ordre : la base, puis « -2 » jusqu'à « -{max} ». */
export function articleCodeCandidates(base: string, max: number = ARTICLE_CODE_SUFFIX_MAX): string[] {
  return [base, ...Array.from({ length: Math.max(0, max - 1) }, (_, i) => suffixedArticleCode(base, i + 2))];
}
